<template>
  <!--
    KPI 报表（/report/kpi）：上查询条件、中左右两个报表、下业绩排名。

    ★ 本次只做右侧「考勤统计」。左侧「销售统计」和底部「销售人员业绩排名」
      先占位（列出位置与空状态），数据与功能后续补。
  -->
  <div class="kpi-report">
    <list-page>
      <!-- ① 查询条件：只有一个时间范围，默认本月 -->
      <search-condition @search="resetSearch" :show-search="true" page-name="kpiReport">
        <template #default>
          <yq-date-range v-model:start-time="searchInfo.startDate" v-model:end-time="searchInfo.endDate">
            <template #prefix>
              <span>考核范围</span>
            </template>
          </yq-date-range>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <!-- ② 中间：左销售统计（占位） + 右考勤统计 -->
      <div class="report-grid">
        <div class="report-card">
          <div class="card-title">销售统计</div>
          <div class="card-placeholder">销售统计数据待补充</div>
        </div>

        <div class="report-card">
          <div class="card-title">
            考勤统计
            <!--
              同步按钮放在考勤模块内部（不在查询条件容器上）：
              它只重拉考勤数据，“强制同步”参数让后端无视「今天已同步过」的标记。
            -->
            <span class="card-actions">
              <page-button size="small" :disabled="loading" @click="loadCalendar(true)">同步数据</page-button>
            </span>
          </div>

          <div v-loading="loading" :element-loading-text="loadingText" class="cal-wrap">
            <!-- 月份头：默认停在查询区间的起始月，可以在区间内翻月（区间外没有拉数据） -->
            <div class="cal-head">
              <div class="cal-title">{{ monthLabel }}</div>
              <div class="cal-nav">
                <span class="nav-btn" :class="{disabled: !canShiftPrev}" title="上月" @click="shiftMonth(-1)">‹</span>
                <span class="nav-btn" title="回到本月" @click="toThisMonth">今</span>
                <span class="nav-btn" :class="{disabled: !canShiftNext}" title="下月" @click="shiftMonth(1)">›</span>
              </div>
            </div>
            <div class="cal-legend">
              <span><i class="dot abnormal"></i>考勤异常</span>
              <span><i class="dot normal"></i>全部正常</span>
            </div>
            <div class="cal-grid">
              <div v-for="d in ['日','一','二','三','四','五','六']" :key="d" class="cal-dow">{{ d }}</div>
              <template v-for="(cell, i) in gridCells" :key="i">
                <div v-if="!cell" class="cal-blank"></div>
                <div v-else class="cal-day"
                     :class="{abnormal: cell.abnormal, 'out-range': cell.outRange}"
                     :title="dayTip(cell)"
                     @click="openDay(cell)">
                  <span class="cal-date">{{ cell.day }}</span>
                  <span v-if="cell.employeeCount" class="cal-count" :class="cell.abnormal ? 'red' : 'green'">
                    {{ cell.employeeCount }}
                  </span>
                  <!-- 右下角假期标识：放假红、调休橙 -->
                  <span v-if="cell.holidayText" class="cal-holiday" :class="cell.holidayType">{{ cell.holidayText }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- ③ 底部：销售人员业绩排名（占位） -->
      <list-table background-color="#FFF">
        <div class="rank-wrap">
          <div class="card-title">销售人员业绩排名</div>
          <el-table :data="[]" border size="small" empty-text="销售人员业绩排名待补充">
            <el-table-column label="排名" prop="index" width="80" align="center"/>
            <el-table-column label="销售人员" prop="employeeName" min-width="160"/>
            <el-table-column label="业绩金额(元)" prop="amount" min-width="160" align="right"/>
          </el-table>
        </div>
      </list-table>
    </list-page>

    <!-- 点日历某一天：抽屉里看当天全部打卡明细 -->
    <el-drawer v-model="showDayDrawer" :title="drawerTitle" direction="rtl" size="40%">
      <div v-loading="dayLoading" class="day-detail">
        <yq-table v-if="dayList.length" :data="dayList" height="auto" page-name="checkInDayDetail" app-name="wx">
          <yq-table-column label="职员" prop="employeeName" min-width="100rem">
            <template v-slot="{row}">{{ row.employeeName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="打卡类型" prop="checkinType" min-width="100rem"/>
          <yq-table-column label="打卡时间" prop="checkinTime" min-width="160rem">
            <template v-slot="{row}">{{ dateTime(row.checkinTime) }}</template>
          </yq-table-column>
          <yq-table-column label="打卡地点" prop="locationTitle" min-width="120rem">
            <template v-slot="{row}">{{ row.locationTitle || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="异常" prop="exceptionType" min-width="120rem">
            <template v-slot="{row}">
              <el-tag v-if="row.exceptionType" size="small" type="danger">{{ row.exceptionType }}</el-tag>
              <span v-else class="muted">正常</span>
            </template>
          </yq-table-column>
        </yq-table>
        <div v-else-if="!dayLoading" class="day-empty">当天没有打卡记录</div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import checkinApi from "@/api/normal/sys/checkinApi";
import calendarHolidayApi from "@/api/dz-crm/kpi/calendarHolidayApi";
import {useRoute, useRouter} from "vue-router";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "KpiReport",
  components: {YqTable, YqTableColumn, PageButton, SearchCondition, ListTable, ListPage, YqDateRange},
  data() {
    const {start} = this_month_range();
    return {
      searchInfo: {
        startDate: "",
        endDate: "",
      },
      // 日历当前展示的月份：查询后由 syncMonthToRange 跟着区间起始月走，区间内可用 ‹ › 翻月
      month: start.slice(0, 7),
      // 后端返回的每天一格：{date, employeeCount, abnormalCount, abnormal}
      dayMap: {},
      holidayMap: {},
      loading: false,
      loadingText: "",
      showDayDrawer: false,
      dayLoading: false,
      dayList: [],
      dayDate: "",
    };
  },
  computed: {
    monthLabel() {
      if (!this.month) {
        return "";
      }
      const [y, m] = this.month.split("-");
      return y + "年 " + m + "月";
    },
    drawerTitle() {
      return "打卡明细 · " + this.dayDate;
    },
    /** 查询区间所在的起始月 / 结束月，翻月只允许在这两个月份之间 */
    minMonth() {
      return this.monthOf(this.searchInfo.startDate) || this.month;
    },
    maxMonth() {
      return this.monthOf(this.searchInfo.endDate) || this.month;
    },
    canShiftPrev() {
      return !!this.month && this.month > this.minMonth;
    },
    canShiftNext() {
      return !!this.month && this.month < this.maxMonth;
    },
    gridCells() {
      const cells = [];
      if (!this.month) {
        return cells;
      }
      const [y, m] = this.month.split("-").map(Number);
      const firstDow = new Date(y, m - 1, 1).getDay();
      const totalDays = new Date(y, m, 0).getDate();
      const rangeStart = this.dateOnly(this.searchInfo.startDate);
      const rangeEnd = this.dateOnly(this.searchInfo.endDate);
      for (let i = 0; i < firstDow; i++) {
        cells.push(null);
      }
      for (let d = 1; d <= totalDays; d++) {
        const date = this.month + "-" + String(d).padStart(2, "0");
        const day = this.dayMap[date];
        const holiday = this.holidayMap[date];
        // 区间外的日子后端根本没查，标灰不可点 —— 否则「没查」会被看成「没打卡」
        const outRange = (!!rangeStart && date < rangeStart) || (!!rangeEnd && date > rangeEnd);
        cells.push({
          date,
          day: d,
          employeeCount: outRange ? 0 : (day ? day.employeeCount : 0),
          // 有异常 → 标红（后端按当天所有人的打卡状态判断）
          abnormal: !outRange && !!(day && day.abnormal),
          outRange,
          holidayText: holiday ? (Number(holiday.dayType) === 1 ? (holiday.holidayName || "休") : "调休") : "",
          holidayType: holiday ? (Number(holiday.dayType) === 1 ? "off" : "work") : "",
        });
      }
      return cells;
    },
  },
  beforeRouteLeave(to, from, next) {
    // 查询条件与日历都在 $data 里，交给统一缓存
    next();
  },
  created() {
    // 默认时间：本月
    let date = new Date();
    this.searchInfo.startDate = date.format("yyyy-MM-01");
    this.searchInfo.endDate = date.format("yyyy-MM-dd");
    this.resetSearch();
  },
  methods: {
    dateTime(v) {
      return v ? String(v).slice(0, 19) : "—";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    monthOf(v) {
      return v ? String(v).slice(0, 7) : "";
    },
    resetSearch() {
      this.syncMonthToRange();
      this.loadHoliday();
      this.loadCalendar(false);
    },
    /**
     * 查询区间变了，日历跟着跳到区间起始月
     *
     * ★ 原来 month 只在 data() 里按「今天」算了一次，换了时间范围日历还停在旧月份，
     *   区间外的日子自然全是空的 —— 看着就像日历加载错了。
     */
    syncMonthToRange() {
      const start = this.monthOf(this.searchInfo.startDate);
      if (start) {
        this.month = start;
      }
    },
    /** 翻月：只在查询区间覆盖到的月份之间翻，翻出去没有任何数据 */
    shiftMonth(step) {
      if (!this.month) {
        return;
      }
      const [y, m] = this.month.split("-").map(Number);
      const target = new Date(y, m - 1 + step, 1);
      const month = target.getFullYear() + "-" + String(target.getMonth() + 1).padStart(2, "0");
      if (month < this.minMonth || month > this.maxMonth) {
        return;
      }
      this.month = month;
    },
    /** 回到本月；本月不在查询区间内时，落到区间最近的一端 */
    toThisMonth() {
      const now = new Date();
      const month = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
      if (month < this.minMonth) {
        this.month = this.minMonth;
      } else if (month > this.maxMonth) {
        this.month = this.maxMonth;
      } else {
        this.month = month;
      }
    },
    /**
     * 拉考勤日历
     *
     * ★ 从企业微信拉数据要等，所以整块盖一层 loading 并写明「正在从企业微信获取数据」——
     *   不然用户看到的就是页面卡住不动。
     */
    async loadCalendar(forceSync) {
      if (!this.searchInfo.startDate || !this.searchInfo.endDate) {
        this.$message.error("请选择考核范围");
        return;
      }
      this.loading = true;
      this.loadingText = forceSync === true ? "正在从企业微信同步数据" : "正在从企业微信获取数据";
      try {
        const list = await checkinApi.queryCalendar({
          ...this.searchInfo,
          forceSync: forceSync === true,
        }) || [];
        const map = {};
        list.forEach(day => map[day.date] = day);
        this.dayMap = map;
      } finally {
        this.loading = false;
        this.loadingText = "";
      }
    },
    /**
     * 假期按年拉，和日历同一个口径（放假/调休当天要标出来）
     *
     * ★ 查询区间可能跨年（比如选了去年 12 月 ~ 今年 1 月），所以按区间覆盖到的年份拉，
     *   不然翻到另一年的月份就没有假期标识了。
     */
    loadHoliday() {
      const startYear = Number(this.dateOnly(this.searchInfo.startDate).slice(0, 4)) || new Date().getFullYear();
      const endYear = Number(this.dateOnly(this.searchInfo.endDate).slice(0, 4)) || startYear;
      const years = [];
      for (let y = startYear; y <= endYear; y++) {
        years.push(y);
      }
      Promise.all(years.map(year => calendarHolidayApi.queryByYear(year).catch(() => []))).then(results => {
        const map = {};
        results.forEach(list => (list || []).forEach(h => map[this.dateOnly(h.holidayDate)] = h));
        this.holidayMap = map;
      });
    },
    dayTip(cell) {
      if (cell.outRange) {
        return "不在考核范围内";
      }
      const parts = [];
      if (cell.holidayText) {
        parts.push(cell.holidayType === "off" ? "放假：" + cell.holidayText : "调休上班");
      }
      if (cell.employeeCount) {
        parts.push(cell.employeeCount + " 人有打卡" + (cell.abnormal ? "（存在异常）" : "（全部正常）"));
      }
      return parts.join(" · ");
    },

    /** 点某天：抽屉里看当天全部打卡明细（后端已按职员 + 打卡时间排好序） */
    async openDay(cell) {
      if (!cell || cell.outRange) {
        return;
      }
      const date = cell.date;
      this.dayDate = date;
      this.dayList = [];
      this.showDayDrawer = true;
      this.dayLoading = true;
      try {
        this.dayList = await checkinApi.queryDayDetail(date) || [];
      } finally {
        this.dayLoading = false;
      }
    },
  },
};

/** 本月一号 ~ 今天。放在组件外，data() 里初始化默认值时就要用 */
function this_month_range() {
  const now = new Date();
  const pad = n => String(n).padStart(2, "0");
  const start = now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-01";
  const end = now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate());
  return {start, end};
}
</script>

<style lang="scss" scoped>
.kpi-report {
  height: 100%;
}

.report-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin: 10px 0;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.report-card,
.rank-wrap {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  background: #fff;
  padding: 12px 14px;
  box-sizing: border-box;
  min-height: 220px;
}

.rank-wrap {
  margin: 10px 0;
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.card-actions {
  display: inline-flex;
  gap: 8px;
}

.card-placeholder {
  padding: 60px 0;
  text-align: center;
  font-size: 12px;
  color: #c0c4cc;
}

/* ============ 日历 ============ */
.cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;

  .cal-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }

  .cal-nav {
    display: flex;
    align-items: center;
    gap: 2px;

    .nav-btn {
      min-width: 22px;
      height: 22px;
      padding: 0 4px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #909399;
      font-size: 13px;
      user-select: none;

      &:hover {
        background: #f2f6ff;
        color: var(--el-color-primary);
      }

      // 已经翻到查询区间的头/尾
      &.disabled {
        color: #dcdfe6;
        cursor: not-allowed;

        &:hover {
          background: transparent;
          color: #dcdfe6;
        }
      }
    }
  }
}

.cal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 11.5px;
  color: #909399;
  padding-bottom: 6px;

  .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;

    &.abnormal {background: #f56c6c;}
    &.normal {background: #67c23a;}
  }
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;

  .cal-dow {
    text-align: center;
    font-size: 11.5px;
    color: #909399;
    padding: 2px 0 4px;
  }

  .cal-blank {
    min-height: 46px;
  }

  .cal-day {
    position: relative;
    min-height: 46px;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 7px;
    padding: 4px 6px;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
    transition: .14s;

    &:hover {
      border-color: var(--el-color-primary);
      background: #f7faff;
    }

    .cal-date {
      font-size: 12.5px;
      font-weight: 600;
      color: #303133;
    }

    // 有打卡记录才给数字，没人打卡的日子保持干净
    .cal-count {
      flex: none;
      font-size: 10px;
      line-height: 15px;
      min-width: 15px;
      text-align: center;
      border-radius: 8px;
      padding: 0 4px;
      color: #fff;

      &.green {background: #67c23a;}
      &.red {background: #f56c6c;}
    }

    // ★ 需求：当天考勤有异常 → 整格标红；全部正常 → 正常样式
    &.abnormal {
      border-color: #f56c6c;
      background: #fef0f0;

      .cal-date {color: #f56c6c;}
    }

    // 查询区间外的日子：没拉过数据，弱化并且不给点
    &.out-range {
      border-color: #f2f3f5;
      background: #fafafa;
      cursor: default;

      &:hover {
        border-color: #f2f3f5;
        background: #fafafa;
      }

      .cal-date {color: #c0c4cc;}

      .cal-holiday {opacity: .5;}
    }

    .cal-holiday {
      position: absolute;
      right: 4px;
      bottom: 3px;
      max-width: calc(100% - 8px);
      font-size: 10px;
      line-height: 14px;
      padding: 0 4px;
      border-radius: 3px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.off {color: #f56c6c; background: #fef0f0;}
      &.work {color: #e6a23c; background: #fdf6ec;}
    }

  }
}

/* ============ 明细抽屉 ============ */
.day-detail {
  min-height: 200px;
}

.day-empty {
  padding: 60px 0;
  text-align: center;
  font-size: 13px;
  color: #c0c4cc;
}

.muted {
  color: #909399;
}
</style>
